import { firefuncs, auth, db } from "config/firebase";
import { httpsCallable } from "firebase/functions";
import {
  GoogleAuthProvider,
  signInWithPopup,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  sendEmailVerification as firebaseSendEmailVerification,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { addUserAndDocsToDB } from "_DatabaseFunctions/directConnect/users";
import { getAuthErrorMessage, validateAuthInput } from "utils/authErrorMessages";

interface SignUpData {
  email: string;
  password: string;
  displayName: string;
}

export const createUser = async (signUpData: SignUpData): Promise<any> => {
  console.log("Creating user with data:", signUpData);

  // Validate input using the new utility
  const validationErrors = validateAuthInput({
    email: signUpData.email,
    password: signUpData.password,
    displayName: signUpData.displayName,
  });

  if (Object.keys(validationErrors).length > 0) {
    // Return the first validation error
    const firstError = Object.values(validationErrors)[0];
    throw new Error(firstError);
  }

  // Clean up data before sending
  const cleanedData = {
    email: signUpData.email.trim(),
    password: signUpData.password,
    displayName: signUpData.displayName.trim(),
  };

  console.log("Sending to createUser function:", cleanedData);

  const createUserFunc = httpsCallable(firefuncs, "createUser");

  try {
    const res = await createUserFunc(cleanedData);
    console.log("create User res", res.data);

    // Check if response follows the new format (direct success)
    if (res.data && res.data.status === "success") {
      return res.data.data;
    }

    // For any other response format, return the data directly
    return res.data;
  } catch (error: any) {
    console.log("Error creating user:", error);
    
    // Use the error message utility for better user experience
    const userFriendlyMessage = getAuthErrorMessage(error);
    throw new Error(userFriendlyMessage);
  }
};

interface EmailVerificationParams {
  email: string;
  displayName: string;
}

interface SendVerificationCodeParams {
  userId: string;
  email: string;
}

interface VerifyEmailCodeParams {
  userId: string;
  code: string;
}

export const sendEmailVerification = async ({
  email,
  displayName,
}: EmailVerificationParams): Promise<any> => {
  console.log("Sending email verification for:", { email, displayName });

  // Validate email format before sending
  const validationErrors = validateAuthInput({ email });
  if (Object.keys(validationErrors).length > 0) {
    throw new Error(validationErrors.email);
  }

  const sendEmail = httpsCallable(firefuncs, "sendEmailVerification");

  try {
    const res = await sendEmail({ email, displayName });
    console.log("send verification email res", res.data);
    return res.data;
  } catch (error: any) {
    console.log("Error sending verification email", error);
    
    // Use the error message utility for better user experience
    const userFriendlyMessage = getAuthErrorMessage(error);
    throw new Error(userFriendlyMessage);
  }
};

// Google Sign-in function
export const signInWithGoogle = async (
  rememberMe: boolean = false,
): Promise<any> => {
  try {
    console.log("Attempting Google sign-in");

    // Set persistence based on user preference
    const persistence = rememberMe
      ? browserLocalPersistence
      : browserSessionPersistence;
    await setPersistence(auth, persistence);

    // Create Google provider
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: "select_account",
    });

    // Sign in with popup
    const result = await signInWithPopup(auth, provider);
    const { user } = result;

    console.log("Google sign-in successful:", user.email);

    // Check if user document exists in Firestore
    const userDocRef = doc(db, "users", user.uid);
    const userDocSnap = await getDoc(userDocRef);

    if (!userDocSnap.exists()) {
      console.log("User document doesn't exist, creating new user document...");
      // Create user document with Google profile information
      await addUserAndDocsToDB({
        user,
      });
      console.log("User document created successfully for Google user");
    } else {
      console.log("User document already exists, sign-in complete");
    }

    return user;
  } catch (error: any) {
    console.error("Google sign-in error:", error);
    
    // Use the error message utility for better user experience
    const userFriendlyMessage = getAuthErrorMessage(error);
    throw new Error(userFriendlyMessage);
  }
};

// Send verification code via cloud function
export const sendVerificationCode = async ({
  userId,
  email,
}: SendVerificationCodeParams): Promise<any> => {
  console.log("Sending verification code for:", { userId, email });

  // Validate inputs
  if (!userId || typeof userId !== "string") {
    throw new Error("User ID is required");
  }
  
  const validationErrors = validateAuthInput({ email });
  if (Object.keys(validationErrors).length > 0) {
    throw new Error(validationErrors.email);
  }

  const sendCodeFunc = httpsCallable(firefuncs, "sendVerificationCode");

  try {
    const res = await sendCodeFunc({ userId, email });
    console.log("send verification code res", res.data);
    return res.data;
  } catch (error: any) {
    console.log("Error sending verification code", error);
    
    // Use the error message utility for better user experience
    const userFriendlyMessage = getAuthErrorMessage(error);
    throw new Error(userFriendlyMessage);
  }
};

// Verify email code via cloud function
export const verifyEmailCode = async ({
  userId,
  code,
}: VerifyEmailCodeParams): Promise<any> => {
  console.log("Verifying email code for user:", userId);

  // Validate inputs
  if (!userId || typeof userId !== "string") {
    throw new Error("User ID is required");
  }
  if (!code || typeof code !== "string") {
    throw new Error("Verification code is required");
  }
  if (code.length !== 6 || !/^\d{6}$/.test(code)) {
    throw new Error("Please enter a valid 6-digit verification code");
  }

  const verifyCodeFunc = httpsCallable(firefuncs, "verifyEmailCode");

  try {
    const res = await verifyCodeFunc({ userId, code });
    console.log("verify email code res", res.data);
    return res.data;
  } catch (error: any) {
    console.log("Error verifying email code", error);
    
    // Use the error message utility for better user experience
    const userFriendlyMessage = getAuthErrorMessage(error);
    throw new Error(userFriendlyMessage);
  }
};

// Alternative email verification using Firebase Auth directly (deprecated)
export const sendEmailVerificationDirect = async (): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("Please sign in to send verification email");
    }

    console.log("Sending email verification for user:", user.email);
    await firebaseSendEmailVerification(user);
    console.log("Email verification sent successfully");
  } catch (error: any) {
    console.error("Error sending email verification:", error);
    
    // Use the error message utility for better user experience
    const userFriendlyMessage = getAuthErrorMessage(error);
    throw new Error(userFriendlyMessage);
  }
};

export default {
  createUser,
  sendEmailVerification,
  sendEmailVerificationDirect,
  sendVerificationCode,
  verifyEmailCode,
  signInWithGoogle,
};
